import React from "react";
import {
  Box,
  Chip,
  colors,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import TrainIcon from "@mui/icons-material/Train";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import GroupsIcon from "@mui/icons-material/Groups";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import HandymanIcon from "@mui/icons-material/Handyman";
import EngineeringIcon from "@mui/icons-material/Engineering";
import { useTranslation } from "../i18n/useTranslation";
import { getZoneTypeVisual } from "../utils/zoneTypeVisuals";

const TrainCard = ({ train, hideProgress = false }) => {
  const { t, language } = useTranslation();
  const { type } = train;
  const routeColor = train.routeColor || type.color;
  const gradient = `linear-gradient(135deg, ${type.accent}, rgba(255,255,255,0.95))`;
  const routeLabel = train.routeName
    ? t("train.route", { name: train.routeName })
    : t("train.unassignedRoute");
  const geofencesToDisplay = Array.isArray(train.geofences)
    ? train.geofences.filter((zone) => !zone?.isStation && zone?.name)
    : [];
  const locationLabel = train.isInsideKnownGeofence ? train.stationName : routeLabel;
  const showGeofences = !train.isInsideKnownGeofence && geofencesToDisplay.length > 0;
  const isInTransit = !train.isInsideKnownGeofence;
  const locale = language === "ar" ? "ar-IQ" : undefined;
  const showProgress = !hideProgress && typeof train.progress === "number";

  const statusLabel = train.isOffline ? t("train.offline") : t("train.online");
  const now = Date.now();
  const diffMs = Math.max(0, now - (train.lastMessageAt || now));
  const diffDays = Math.max(1, Math.ceil(diffMs / (24 * 60 * 60 * 1000)));
  const lastMessageLabel = t("train.lastMessageDays", { value: diffDays });

  const showLastStationDetails =
    !train.isOffline &&
    isInTransit &&
    Boolean(train.lastKnownStationName) &&
    typeof train.lastKnownStationAt === "number";

  const formattedLastStationTime = showLastStationDetails
    ? new Date(train.lastKnownStationAt).toLocaleString(locale, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : "";

  const typeIcons = {
    passenger: <GroupsIcon sx={{ color: routeColor }} />,
    freight: <LocalShippingIcon sx={{ color: routeColor }} />,
    fuel: <LocalGasStationIcon sx={{ color: routeColor }} />,
    alignment: <HandymanIcon sx={{ color: routeColor }} />,
    civil: <EngineeringIcon sx={{ color: routeColor }} />,
    other: <TrainIcon sx={{ color: routeColor }} />,
  };

  const fallbackKey =
    typeof train.typeId === "string"
      ? train.typeId
      : train.typeId === 1
      ? "passenger"
      : train.typeId === 2
      ? "freight"
      : "other";
  const resolvedTypeKey =
    (typeof type.localizationKey === "string" && type.localizationKey) || fallbackKey;
  const typeIcon = typeIcons[resolvedTypeKey] || typeIcons.other;

  return (
    <Paper
      elevation={0}
      className={isInTransit ? "train-card train-card--intransit" : "train-card"}
      sx={{
        p: 3,
        borderRadius: 2,
        bgcolor: gradient,
        border: `1px solid ${type.color}33`,
        minHeight: 220,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        opacity: train.isOffline ? 0.8 : 1,
        position: "relative",
        overflow: "visible",
        "--train-card-pulse-color": routeColor,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              bgcolor: `${routeColor}22`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {typeIcon}
          </Box>
          <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
            {type.label}
          </Typography>
        </Stack>

        <Chip
          label={statusLabel}
          size="small"
          color={train.isOffline ? "default" : "success"}
          variant={train.isOffline ? "outlined" : "filled"}
          sx={{ fontWeight: 600, alignSelf: "center" }}
        />
      </Stack>

      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          px: 2,
          py: 0.5,
          borderRadius: 999,
          bgcolor: routeColor,
          color: "#fff",
          fontWeight: 700,
          boxShadow: "0 4px 14px rgba(15,23,42,0.18)",
          alignSelf: "flex-start",
        }}
      >
        {train.code}
      </Box>

      <Stack direction="row" spacing={1} alignItems="flex-start">
        <LocationOnIcon color="action" sx={{ mt: 0.2 }} />
        <Box>
          <Typography variant="body2" color="text.secondary">
            {locationLabel}
          </Typography>
          {train.isInsideKnownGeofence && (
            <Typography variant="caption" color="text.secondary">
              {routeLabel}
            </Typography>
          )}
          {showGeofences && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                mt: 0.75,
              }}
            >
              {geofencesToDisplay.map((zone, index) => {
                const { Icon, color } = getZoneTypeVisual(zone.type);
                const key = zone.id ?? `${zone.name}-${index}`;
                return (
                  <Box
                    key={key}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.75,
                      px: 1.25,
                      py: 0.5,
                      borderRadius: 999,
                      bgcolor: "rgba(37, 99, 235, 0.08)",
                    }}
                  >
                    <Icon sx={{ fontSize: 14, color }} />
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, color: "text.secondary" }}
                    >
                      {zone.name}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Stack>

      {showLastStationDetails && (
        <>
          <Box
            sx={{
              height: 1,
              bgcolor: "rgba(15,23,42,0.15)",
              borderRadius: 0.5,
              my: 0.5,
            }}
          />
          <Stack spacing={0.5}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {t("train.lastStationLabel")}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
              {train.lastKnownStationName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("train.lastStationTime", { time: formattedLastStationTime })}
            </Typography>
          </Stack>
        </>
      )}

      {showProgress && (
        <Stack spacing={0.5}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              {t("train.journeyProgress")}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {train.progress}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={train.progress}
            sx={{
              height: 8,
              borderRadius: 999,
              bgcolor: `${routeColor}22`,
              "& .MuiLinearProgress-bar": {
                borderRadius: 999,
                bgcolor: routeColor,
              },
            }}
          />
        </Stack>
      )}

      {train.isOffline && (
        <Typography variant="caption" color="text.secondary">
          {lastMessageLabel}
        </Typography>
      )}
    </Paper>
  );
};

export default TrainCard;
